import { useState } from 'react';
import type { ThreadTopicSummary } from '@shared/types/thread-work';
import { planTopic, type TopicPlan } from '@/lib/topic-plan';
import type { WorkGuidanceMotif } from '@/lib/work-guidance';
import { GuidanceMotifItems } from './GuidanceMotifItems';
import { PassToForm } from './PassToForm';
import { quickDates, type TodayBucket, type TodayCommitment } from './today-horizons';

const ITEM_CLASS = 'flex w-full items-center px-3 py-2 text-left text-sm text-domino-ink hover:bg-domino-muted disabled:opacity-50';

/** Menu entries to take on, plan, pass (relais) or drop a topic (J5). */
export const TopicPlanItems = ({ topic, commitment, bucket, onDone }: {
  topic: ThreadTopicSummary;
  commitment: TodayCommitment;
  bucket: TodayBucket;
  onDone: () => void;
}) => {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [date, setDate] = useState('');
  const [passing, setPassing] = useState(false);
  const { tomorrow, monday } = quickDates(new Date());
  const plan = async (
    horizon: TopicPlan,
    plannedFor: string | null = null,
    guidance: { motif: WorkGuidanceMotif; note: string | null } | null = null,
  ) => {
    setPending(true);
    setError(null);
    try {
      await planTopic({
        topicId: topic.id, expectedTopicVersion: topic.version, horizon, plannedFor,
        ...(guidance ? { motif: guidance.motif, note: guidance.note } : {}),
      });
      onDone();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Le travail n’a pas pu être planifié.');
    } finally {
      setPending(false);
    }
  };
  if (passing) return <PassToForm topic={topic} onDone={onDone} />;
  return (
    <>
    <div className="border-b border-domino-cardBorder py-1">
      <button type="button" disabled={pending} onClick={() => setPassing(true)} className={ITEM_CLASS}>Passer à…</button>
      {commitment === 'suggestion' && (
        <button type="button" disabled={pending} onClick={() => void plan('today')} className={ITEM_CLASS}>Accepter</button>
      )}
      {commitment === 'engagement' && bucket !== 'today' && (
        <button type="button" disabled={pending} onClick={() => void plan('today')} className={ITEM_CLASS}>Aujourd’hui</button>
      )}
      <button type="button" disabled={pending} onClick={() => void plan('later', tomorrow)} className={ITEM_CLASS}>Demain</button>
      <button type="button" disabled={pending} onClick={() => void plan('later', monday)} className={ITEM_CLASS}>Lundi</button>
      <div className="flex items-center gap-2 px-3 py-1.5">
        <input
          type="date"
          aria-label="Date"
          min={tomorrow}
          value={date}
          onChange={(event) => setDate(event.target.value)}
          className="min-w-0 flex-1 rounded border border-domino-cardBorder px-2 py-1 text-xs"
        />
        <button type="button" disabled={pending || !date} onClick={() => void plan('later', date)} className="text-sm text-domino-aubergine disabled:opacity-40">OK</button>
      </div>
      {bucket !== 'someday' && (
        <button type="button" disabled={pending} onClick={() => void plan('someday')} className={ITEM_CLASS}>Un jour</button>
      )}
      {error && <p role="alert" className="px-3 py-1 text-xs text-red-700">{error}</p>}
    </div>
    <GuidanceMotifItems
      disabled={pending}
      onPick={(motif, note) => void plan(motif === 'done' ? 'done' : 'dropped', null, { motif, note })}
    />
    </>
  );
};
